import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo, memo } from 'react';
import { Form, Row, Col, Button, Card, Image, OverlayTrigger} from 'react-bootstrap';
import currencies from './data/currencies'
import { validateURL} from './utils/validation';
import icValidationError from './assets/img/ic-danger.svg';
import icInfo from './assets/img/ic-info-circle.svg';
import Tooltip from 'react-bootstrap/Tooltip';
import icCity from './assets/img/ic-city.svg';
import icNotes from './assets/img/ic-news.svg';


const CitySalary = forwardRef(({ formData, handleChange, errors, handleSubmit, isEditPage, isReviewPage, onValidationChange, onShowErrorMessage }, ref) => {
    
    const [isEditing, setIsEditing] = useState(!isEditPage); // If adding, fields start enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // Store initial values
    const [localErrors, setLocalErrors] = useState({}); // ✅ Renamed errors state to avoid conflict
    const [govUrl, setGovUrl] = useState(() => (formData?.govUrl || ''));
    const [legalUrl, setLegalUrl] = useState(() => (formData?.legalUrl || ''));
    const [touched, setTouched] = useState({ govUrl:false, legalUrl:false });

    const urlError = (v) => (v && !validateURL(v) ? 'Please enter a valid URL.' : '');

    const setFieldError = (key, msg) =>
    setLocalErrors(prev => ({ ...prev, [key]: msg || '' }));

    // ✅ Compute whether we have any local or parent-provided errors
    const hasLocalErrors = !!Object.values(localErrors || {}).some(Boolean);
    const hasParentErrors = !!Object.values(errors || {}).some(Boolean);
    const hasAnyErrors = hasLocalErrors || hasParentErrors; // ✅

    // ✅ normalize + validate
    const normalizeUrl = (raw) => {
        const s = (raw || "").trim();
        if (!s) return null;
        const withScheme = /^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(s) ? s : `https://${s}`;
        try {
            const u = new URL(withScheme);
            return ["http:", "https:"].includes(u.protocol) ? u.toString() : null;
        } catch {
            return null;
        }
    };

    // small helper hook
    const useSafeHref = (raw) => React.useMemo(() => normalizeUrl(raw), [raw]);

    // inside your component:
    const govHref   = useSafeHref(formData.govUrl);
    const legalHref = useSafeHref(formData.legalUrl);

    // Pull symbol from the imported currencies list based on the selected code
    const currencySymbol = useMemo(() => {
        // default to EUR if nothing selected
        const code = formData?.currency || 'EUR';

        // Find the matching currency entry (supports both object and string shapes)
        const entry = Array.isArray(currencies)
            ? currencies.find(c => (typeof c === 'string' ? c === code : c?.value === code))
            : null;

        // Prefer label like "€ - Euro" → take everything before " - "
        const label = typeof entry === 'string' ? entry : entry?.label;
        if (typeof label === 'string' && label.length) {
            const beforeDash = label.split(' - ')[0].trim();
            return beforeDash || code;
        }

        // Final fallback
        return (typeof entry === 'string' ? entry : entry?.value) || code || '€';
    }, [formData?.currency]);

    // ✅ Notify parent whenever error state changes
    useEffect(() => {
        if (typeof onValidationChange === 'function') {
            onValidationChange(hasAnyErrors);
        }
    }, [hasAnyErrors, onValidationChange]);

    useEffect(() => {
        if (formData?.govUrl !== undefined) {
          setGovUrl(formData.govUrl);
        }
      }, [formData?.govUrl]);

    const handleGovURLChange = (e) => {
        const { value } = e.target;
        setGovUrl(value);

        clearTimeout(window.govUrlValidationTimeout);
        window.govUrlValidationTimeout = setTimeout(() => {
            const msg = urlError(value);
            setFieldError('govUrl', msg);
            if (!msg) {
            handleChange({ target: { name: 'govUrl', value } });
            }
        }, 600);
    };

    const handleGovURLBlur = () => {
        setTouched(t => ({ ...t, govUrl: true }));
        const msg = urlError(govUrl);
        setFieldError('govUrl', msg);
        if (!msg) {
            handleChange({ target: { name: 'govUrl', value: govUrl } });
        }
    };

    useEffect(() => {
        if (formData?.legalUrl !== undefined) {
          setLegalUrl(formData.legalUrl);
        }
      }, [formData?.legalUrl]);

    // ✅ expose a synchronous validator to the parent (flush debounced checks)
    useImperativeHandle(ref, () => ({
        validateNow: () => {
            const nextLocal = { ...localErrors };
            if (govUrl && !validateURL(govUrl)) nextLocal.govUrl = 'Please enter a valid URL.';
            if (legalUrl && !validateURL(legalUrl)) nextLocal.legalUrl = 'Please enter a valid URL.';
            setTouched(t => ({ ...t, govUrl: true, legalUrl: true }));
            // remove empty strings (treat as no error)
            Object.keys(nextLocal).forEach(k => { if (!nextLocal[k]) delete nextLocal[k]; });
            setLocalErrors(prev => ({ ...prev, ...nextLocal }));
            const any = !!Object.values({ ...errors, ...nextLocal }).some(Boolean);
            onValidationChange?.(any);
            return !any; // true = OK to submit
        }
    }));

    const handleLegalURLChange = (e) => {
        const { value } = e.target;
        setLegalUrl(value);

        clearTimeout(window.legalUrlValidationTimeout);
        window.legalUrlValidationTimeout = setTimeout(() => {
            setFieldError('legalUrl', urlError(value));
            if (!urlError(value)) {
            handleChange({ target: { name: 'legalUrl', value } });
            }
        }, 600);
    };
    
    const handleLegalURLBlur = () => {
        setTouched(t => ({ ...t, legalUrl: true }));
        const msg = urlError(legalUrl);
        setFieldError('legalUrl', msg);
        // push value up if valid on blur
        if (!msg) handleChange({ target: { name: 'legalUrl', value: legalUrl } });
    };


    // Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // Store previous values
    };

    // Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();

        // ✅ cancel pending debounce validations
        if (window.govUrlValidationTimeout) clearTimeout(window.govUrlValidationTimeout);
        if (window.legalUrlValidationTimeout) clearTimeout(window.legalUrlValidationTimeout);

        const g = (govUrl || '').trim();
        const l = (legalUrl || '').trim();

        // ✅ allow empty, block invalid
        const govMsg = g && !validateURL(g) ? 'Please enter a valid URL.' : '';
        const legalMsg = l && !validateURL(l) ? 'Please enter a valid URL.' : '';

        setTouched(t => ({ ...t, govUrl: true, legalUrl: true }));
        setFieldError('govUrl', govMsg);
        setFieldError('legalUrl', legalMsg);

        if (govMsg || legalMsg) {
            onValidationChange?.(true);
            onShowErrorMessage?.(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return; // ✅ BLOCK save
        }

        // ✅ push up (empty or valid) before submit
        handleChange({ target: { name: 'govUrl', value: g } });
        handleChange({ target: { name: 'legalUrl', value: l } });

        if (typeof handleSubmit === 'function') {
            await handleSubmit(e);
            onShowErrorMessage?.(false); // ✅ clear the top Alert
            onValidationChange?.(false); // ✅ optional: also clear child error flag
            setIsEditing(false);
        } else {
            console.error('Error: handleSubmit is not a function.');
        }
    };

    // Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);
        handleChange({ target: { name: 'formData', value: tempFormData } }); // Restore previous values
    };

    // ✅ helpers for thousands formatting
    const toDigits = (val) => {
        if (val === null || val === undefined) return '';
        return String(val).replace(/[^\d]/g, '');
    };

    const formatThousands = (digits) => {
        if (!digits) return '';
        const str = String(digits);
        // 52131 -> 52'131
        return str.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    };

    // ✅ 1) helpers & config
    const pillars = [
        { key: 'central',   label: 'Central functions' },
        { key: 'production',label: 'Operations/production' },
        { key: 'it',        label: 'IT' },
    ];

    // ✅ bands with JSX labels
    const bands = [
        {
            label: <>Student jobs <span className="label-sub">/ Hourly</span>
            <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                <Image src={icInfo} alt="Additional Info" />
            </OverlayTrigger>
            </>,
            prefix: '' // ✅ keeps centralFrom / centralTo / productionFrom / itFrom...
        },
        {
            label: <>Intern jobs <span className="label-sub">/ Hourly</span>
            <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                <Image src={icInfo} alt="Additional Info" />
            </OverlayTrigger>
            </>,
            prefix: 'intern' // ✅ internCentralFrom / internCentralTo / ...
        },
        {
            label: <>1–3 year jobs <span className="label-sub">/ Hourly</span>
            <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                <Image src={icInfo} alt="Additional Info" />
            </OverlayTrigger>
            </>,
            prefix: 'oneThree' // ✅ oneThreeCentralFrom / oneThreeProductionTo / ...
        },
        {
            label: <>3+ year jobs <span className="label-sub">/ Hourly</span>
            <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                <Image src={icInfo} alt="Additional Info" />
            </OverlayTrigger>
            </>,
            prefix: 'three' // ✅ threeCentralFrom / threeItTo / ...
        },
        {
            label: <>Uplift from Top Payers <span className="label-sub">/ Hourly</span>
            <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                <Image src={icInfo} alt="Additional Info" />
            </OverlayTrigger>
            </>,
            prefix: 'uplift',
            isPercent: true
        },
    ];


    // ✅ 2) field name builder that matches your current names
    const cap = s => s.charAt(0).toUpperCase() + s.slice(1);
    const fieldName = (bandPrefix, pillarKey, side/* 'From' | 'To' */) =>
        bandPrefix === 'uplift'
            ? `${bandPrefix}${cap(pillarKey)}`                      // upliftCentral / upliftProduction / upliftIt
            : `${bandPrefix}${bandPrefix ? cap(pillarKey) : pillarKey}${side}`;
    //^ no cap for student row pillar in your current names (centralFrom)

    // ✅ 3) tiny input cell
    const RangeCell = memo(function RangeCell({
        name,
        value,
        onChange,          // same prop as before (parent handleChange)
        disabled,
        adornment,
        className = "",
    }) {
        // store RAW digits here
        const [local, setLocal] = useState(() => toDigits(value));

        // Keep local in sync if parent value changes (e.g., after fetch/save)
        useEffect(() => {
            setLocal(toDigits(value));
        }, [value]);

        const handleLocalChange = (e) => {
            // user may type anything, we keep only digits internally
            const digits = toDigits(e.target.value);
            setLocal(digits);
        };

        // Commit to parent only when user leaves the field
        const handleBlur = () => {
            const currentValue = value ?? '';
            const digits = local || '';

            if (digits !== toDigits(currentValue)) {
                // send RAW digits to parent
                onChange({ target: { name, value: digits } });
            }
        };

        // what user sees: formatted with apostrophes
        const displayValue = formatThousands(local);

        return (
            <div className={`as-table d-flex align-items-center justify-between ${className}`}>
                <Form.Control
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    spellCheck={false}
                    name={name}
                    value={displayValue}
                    onChange={handleLocalChange}   // only update local digits
                    onBlur={handleBlur}            // push raw digits to parent once
                    disabled={disabled}
                    onWheel={(e) => e.currentTarget.blur()}
                    style={{ textAlign: "right", flex: 1 }}
                />
                {adornment ? (
                    <span aria-hidden="true" className="mx-1">{adornment}</span>
                ) : null}
            </div>
        );
    });


    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    {/* Title and Edit Button */}
                    <Row className="align-items-center">
                        <Col>
                            {isEditPage && (
                                <div className="float-end">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3>Average salaries</h3>
                            <hr />
                        </Col>
                    </Row>
                    {isEditing ? (
                        <>
                            <Row>
                                {/* Form Fields */}
                                <Col md={12}>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Row className="mb-3">
                                                <Col md={12}>
                                                    <Form.Group controlId="currency">
                                                        <Form.Label>Currency</Form.Label>

                                                        <Form.Select
                                                            name="currency"
                                                            value={formData.currency ?? ''}
                                                            onChange={handleChange}
                                                            disabled={!isEditing}
                                                        >
                                                            <option value="">Select</option>
                                                            {Array.isArray(currencies) &&
                                                            currencies.map(opt => (
                                                                <option key={opt.value ?? opt} value={opt.value ?? opt}>
                                                                {opt.label ?? opt}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col md={12}>
                                                    <Form.Group className="validated" controlId="govUrl">
                                                        <Form.Label>Governmental tools URL</Form.Label>
                                                        <Form.Control
                                                            type="url"
                                                            name="govUrl"
                                                            value={govUrl}
                                                            onChange={handleGovURLChange}
                                                            onBlur={handleGovURLBlur}
                                                            placeholder="example.gov/portal"
                                                            inputMode="url"
                                                            isInvalid={touched.govUrl && !!(localErrors?.govUrl || errors?.govUrl)}
                                                            disabled={!isEditing}
                                                            aria-describedby="govUrlFeedback"
                                                        />
                                                        {!!(localErrors?.govUrl || errors?.govUrl) && (
                                                            <div className="custom-error-icon-wrapper">
                                                                <img
                                                                    src={icValidationError}
                                                                    alt={localErrors?.govUrl || errors?.govUrl}
                                                                    className="custom-error-icon"
                                                                />
                                                                <div className="custom-tooltip">
                                                                    {localErrors?.govUrl || errors?.govUrl}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col md={12}>
                                                    <Form.Group className="validated" controlId="legalUrl">
                                                        <Form.Label>Legal Employment conditions by law URL</Form.Label>
                                                        <Form.Control
                                                            type="url"
                                                            name="legalUrl"
                                                            value={legalUrl}
                                                            onChange={handleLegalURLChange}
                                                            onBlur={handleLegalURLBlur}
                                                            placeholder="example.gov/legal-employment"
                                                            inputMode="url"
                                                            isInvalid={touched.legalUrl && !!(localErrors?.legalUrl || errors?.legalUrl)}
                                                            disabled={!isEditing}
                                                            required
                                                            aria-describedby="legalUrlFeedback"
                                                        />
                                                        {!!(localErrors?.legalUrl || errors?.legalUrl) && (
                                                            <div className="custom-error-icon-wrapper">
                                                                <img
                                                                    src={icValidationError}
                                                                    alt={localErrors?.legalUrl || errors?.legalUrl}
                                                                    className="custom-error-icon"
                                                                />
                                                                <div className="custom-tooltip">
                                                                    {localErrors?.legalUrl || errors?.legalUrl}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group controlId="remarks">
                                                <Form.Label>Remarks</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    name="remarks"
                                                    value={formData.remarks || ''}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                    placeholder="Remarks description goes here..."
                                                    style={{ height: "200px" }}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    
                                    <hr />

                                    {/* Header with 3 pillar cards & From/To sub-headers */}
                                    <Row className="align-items-end">
                                        <Col md={3} />
                                        {pillars.map((p) => (
                                            <Col key={p.key} md={3}>
                                                <div className="salary-table__title">{/* ✅ title bar */}
                                                    <span>{p.label}</span>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip>
                                                                Info text here
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <Image
                                                            src={icInfo}
                                                            alt="Additional Info"
                                                        />
                                                    </OverlayTrigger>
                                                </div>
                                                <div className="salary-table">{/* ✅ new root class */}
                                                    <div className="salary-table__head">{/* ✅ table head (From/To) */}
                                                        <div>From</div>
                                                        <div>To</div>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>

                                    {/* Rows for each band */}
                                    {bands.map((band, idx) => (
                                        <Row
                                            key={`${band.prefix ?? 'student'}-${idx}`}
                                            className={idx === bands.length - 1 ? 'mb-4' : 'mb-0'}
                                        >
                                            {/* Left labels column */}
                                            <Col md={3} className="labels-column">
                                                {band.label}
                                            </Col>

                                            {/* Three pillar columns */}
                                            {pillars.map((p) => {
                                                const isUplift = band.prefix === 'uplift';
                                                const nameFrom = isUplift
                                                    ? fieldName(band.prefix, p.key) // upliftCentral
                                                    : fieldName(band.prefix, p.key, 'From');
                                                const nameTo = isUplift ? null : fieldName(band.prefix, p.key, 'To');

                                                return (
                                                    <Col key={p.key} md={3}>
                                                        {/* ✅ table body grid for this pillar */}
                                                        <div className="salary-table__grid">
                                                            {/* From or single (uplift) */}
                                                            <div
                                                                className={
                                                                    'salary-table__cell' + (isUplift ? ' salary-table__cell--span2' : '')
                                                                }
                                                            >
                                                                <RangeCell
                                                                    name={nameFrom}
                                                                    value={formData[nameFrom]}
                                                                    onChange={handleChange}
                                                                    disabled={!isEditing}
                                                                    adornment={isUplift ? '%' : currencySymbol}
                                                                />
                                                            </div>

                                                            {/* To (omit for uplift row) */}
                                                            {!isUplift && (
                                                                <div className="salary-table__cell">
                                                                    <RangeCell
                                                                        name={nameTo}
                                                                        value={formData[nameTo]}
                                                                        onChange={handleChange}
                                                                        disabled={!isEditing}
                                                                        adornment={currencySymbol}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                    ))}

                                    {isReviewPage && (
                                        <Row>
                                            <Col md={6}>
                                                <p>Under Review</p> {/* ✅ Show only in Review Page */}
                                            </Col>
                                        </Row>
                                    )}
                                </Col>
                            </Row>
                        </>     
                    ) : (
                        <>
                            <Row>
                                <Col md={6}>
                                    <Row className="mb-4">
                                        <Col md={12}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icCity} alt="Currency" />
                                                </span>
                                                <span className="text">
                                                    Currency
                                                    <br />
                                                    <strong>
                                                        {formData.currency || "N/A"}
                                                    </strong>
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row className="mb-4">
                                        <Col md={12}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icCity} alt="Governmental tools URL" />
                                                </span>
                                                <span className="text">
                                                    <span className="d-flex">
                                                        Governmental tools URL
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                                                            <Image src={icInfo} alt="Additional Info" className="ms-2" />
                                                        </OverlayTrigger>
                                                    </span>
                                                    <strong>
                                                        {govHref ? (
                                                            <a href={govHref} target="_blank" rel="noopener noreferrer">{formData.govUrl}</a>
                                                        ) : "N/A"}
                                                    </strong>
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row className="mb-4">
                                        <Col md={12}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icCity} alt="Legal Employment conditions by law URL" />
                                                </span>
                                                <span className="text" >
                                                    <span className="d-flex">
                                                        Legal Employment conditions by law URL 
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                                                            <Image src={icInfo} alt="Additional Info" className="ms-2" />
                                                        </OverlayTrigger>
                                                    </span>
                                                    <strong>
                                                        {legalHref ? (
                                                            <a href={legalHref} target="_blank" rel="noopener noreferrer">{formData.legalUrl}</a>
                                                        ) : "N/A"}
                                                    </strong>
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>      
                                </Col>
                                <Col md={6}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icNotes} alt="Remarks" />
                                        </span>
                                        <span className="text">
                                            Remarks
                                            <br />
                                            <strong>
                                                {formData.remarks || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>

                            <hr />

                            {/* Header with 3 pillar cards & From/To sub-headers */}
                            <Row className="align-items-end">
                                <Col md={3} />
                                {pillars.map((p) => (
                                    <Col key={p.key} md={3}>
                                        <div className="salary-table__title">{/* ✅ title bar */}
                                            <span>{p.label}</span>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip>
                                                        Info text here
                                                    </Tooltip>
                                                }
                                            >
                                                <Image
                                                    src={icInfo}
                                                    alt="Additional Info"
                                                />
                                            </OverlayTrigger>
                                        </div>
                                        <div className="salary-table">{/* ✅ new root class */}
                                            <div className="salary-table__head">{/* ✅ table head (From/To) */}
                                                <div>From</div>
                                                <div>To</div>
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>

                            {/* Rows for each band */}
                            {bands.map((band, idx) => (
                                <Row
                                    key={`${band.prefix ?? 'student'}-${idx}`}
                                    className={idx === bands.length - 1 ? 'mb-4' : 'mb-0'}
                                >
                                    {/* Left labels column */}
                                    <Col md={3} className="labels-column">
                                        {band.label}
                                    </Col>

                                    {/* Three pillar columns */}
                                    {pillars.map((p) => {
                                        const isUplift = band.prefix === 'uplift';
                                        const nameFrom = isUplift
                                            ? fieldName(band.prefix, p.key) // upliftCentral
                                            : fieldName(band.prefix, p.key, 'From');
                                        const nameTo = isUplift ? null : fieldName(band.prefix, p.key, 'To');

                                        return (
                                            <Col key={p.key} md={3}>
                                                {/* ✅ table body grid for this pillar */}
                                                <div className="salary-table__grid">
                                                    {/* From or single (uplift) */}
                                                    <div
                                                        className={
                                                            'salary-table__cell' + (isUplift ? ' salary-table__cell--span2' : '')
                                                        }
                                                    >
                                                        <RangeCell
                                                            name={nameFrom}
                                                            value={formData[nameFrom]}
                                                            onChange={handleChange}
                                                            disabled={!isEditing}
                                                            adornment={isUplift ? '%' : currencySymbol}
                                                        />
                                                    </div>

                                                    {/* To (omit for uplift row) */}
                                                    {!isUplift && (
                                                        <div className="salary-table__cell">
                                                            <RangeCell
                                                                name={nameTo}
                                                                value={formData[nameTo]}
                                                                onChange={handleChange}
                                                                disabled={!isEditing}
                                                                adornment={currencySymbol}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            ))}
                        </>
                    )}
                </Card.Body>
            </Card>    
        </>
    );
});

export default CitySalary;
